import React from 'react';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { useFeatureFlag } from '../../hooks/useFeatureFlag/useFeatureFlag';
import { useGoogleSearchContext } from './GoogleInstantSearch';
import { buildFilterExpression } from '@jbhi-fi/apis/src/utils/filterExpression';
import { getSortOrderLabel } from './getSortOrderLabel';
import { InfiniteResults } from '../../templates/components/search-results/CustomInfiniteHits';
import ResultsSkeleton from '../../templates/components/collections/ResultsSkeleton';
import { NoResultsView } from '../../templates/components/collections/NoResultsView';
import { SearchContentContainer } from '../searchcontent/SearchContentContainer';
import { styles } from '../../templates/components/search-results/ResultsContainer.css';
import CustomRefinementListContainer from '../../templates/components/refinements/CustomRefinementListContainer';
import GoogleRefinementContainer from '../../templates/components/refinements/GoogleRefinementContainer';
import { renderGoogleFacet } from '../../templates/components/refinements/renderGoogleFacet';
import { Disclaimer } from '@jbhi-fi/components/src/Disclaimer';

type Facet = { facet: string; type: string };

type GoogleSearchPanelProps = {
  query: string;
};

const GoogleSearchPanel = ({ query }: GoogleSearchPanelProps) => {
  const {
    hits,
    nbHits,
    facets: facetMap,
    attributionToken,
    isLoading,
    isFetching,
    loadedPageCount,
    isLastPage,
    showMore,
    state,
    usedFilter,
  } = useGoogleSearchContext();

  const isDesktop = useBreakpoint('lg', 'up');

  const isHorizontalProductCardEnabled = useFeatureFlag('isHorizontalProductCardEnabled');
  const isProductCardDetailsEnabled = useFeatureFlag('isProductCardDetailsEnabled');
  const isProductCardDetailBadgesEnabled = useFeatureFlag('isProductCardDetailBadgesEnabled');

  const isDesktopView = useBreakpoint('md', 'up');
  const productCardVariant = (() => {
    if (isHorizontalProductCardEnabled) return 'landscape';
    if (!isDesktopView) return 'tablet';
    return 'default';
  })();

  const HIDDEN_FACETS = ['onPromotion', 'allCalculatedProductTags', 'Category'];
  const BASE_FACETS: Array<Facet> = [
    { facet: 'Sold by', type: 'refinementList' },
    { facet: 'Brand', type: 'refinementList' },
    { facet: 'Price', type: 'rangeInput' },
    { facet: 'Category', type: 'refinementList' },
    { facet: 'Artist', type: 'refinementList' },
    { facet: 'isMarketplace', type: 'toggleRefinement' },
  ];

  const orderedFacetKeys: Array<string> = Object.keys(facetMap);
  const apiFacets: Array<Facet> = orderedFacetKeys
    .filter(rawKey => {
      const stripped = rawKey.replace(/^facets\./, '');
      return !HIDDEN_FACETS.includes(rawKey) && !HIDDEN_FACETS.includes(stripped);
    })
    .map(rawKey => {
      const facetName = rawKey.replace(/^facets\./, '');
      const values = facetMap[rawKey] ?? facetMap[`facets.${facetName}`] ?? {};
      if (!Object.keys(values).length) return null;
      return {
        facet: facetName,
        type: facetName === 'Price' ? 'rangeInput' : 'refinementList',
      };
    })
    .filter((f): f is Facet => f !== null);

  const baseFacetNames = new Set(BASE_FACETS.map(f => f.facet.toLowerCase()));
  const extraFacets = apiFacets.filter(f => !baseFacetNames.has(f.facet.toLowerCase()));
  const googleFacets = [...BASE_FACETS, ...extraFacets];

  const renderHits = () => {
    if (isLoading && hits.length === 0) {
      return (
        <div className={`search-results-loop search-results-loop--${productCardVariant}`}>
          <ResultsSkeleton shouldShimmer variant={productCardVariant} />
        </div>
      );
    }
    if (!isLoading && hits.length === 0) {
      return (
        <NoResultsView title="Sorry, we weren't able to find any results for your search.">
          <button onClick={() => document.querySelector<HTMLInputElement>('#quicksearch-search-box input')?.click()}>
            Start a new search
          </button>
          <h4>Tips to improve your search:</h4>
          <ul>
            <li>Check your spelling.</li>
            <li>Try less specific words.</li>
            <li>Try some alternative or substitute words.</li>
            {window.Jbhifi?.letUsKnowUrl ? (
              <li>
                Looking for something awesome that we don’t stock yet?{' '}
                <a style={{ textDecoration: 'underline', display: 'inline' }} href={window.Jbhifi?.letUsKnowUrl}>
                  Let us know!
                </a>
              </li>
            ) : null}
          </ul>
        </NoResultsView>
      );
    }
    return (
      <InfiniteResults
        nbHits={nbHits}
        page={Math.max(0, loadedPageCount - 1)}
        hits={hits}
        hitsPerPage={state.hitsPerPage}
        showMore={showMore}
        isLastPage={isLastPage}
        isLoading={isFetching}
        productCardVariant={productCardVariant}
        isProductCardDetailsEnabled={isProductCardDetailsEnabled}
        isProductCardDetailBadgesEnabled={isProductCardDetailBadgesEnabled}
        cloudRetail={{
          attributionToken,
          filter: usedFilter || undefined,
          sortOrder: getSortOrderLabel(state.sortBy, state.order),
        }}
      />
    );
  };

  return (
    <>
      {nbHits > 0 || !isLoading ? (
        <h3 className="search-title">{`${nbHits} result${nbHits === 1 ? '' : 's'} for "${query}"`}</h3>
      ) : (
        <div
          style={{
            width: '100%',
            maxWidth: '400px',
            height: '40px',
            backgroundColor: '#EEEEEE',
            margin: '0 0 17.5px 0',
          }}
        />
      )}

      <SearchContentContainer term={query} />

      <GoogleRefinementContainer facets={googleFacets} isSearchPage isDesktop={isDesktop} />

      <div className={`${styles.resultsContainer} search-refinement-container`}>
        {isDesktop ? (
          <div className={styles.resultsRefinementContainer}>
            <CustomRefinementListContainer
              initialFacets={googleFacets}
              nbHits={nbHits}
              priceRange={{ min: 0, max: 0 }}
              mobileShow={false}
              setMobileShowFilter={() => undefined}
              trackingInfo={{ defaults: { hitsPerPage: 36, sortBy: 'relevancy' } }}
              isSearchPage
              isSticky={false}
              nbSaleItems={0}
              nbClearanceItems={0}
              renderFacet={(f, t, i, s, o) => renderGoogleFacet(f, t, i, s, o, true)}
            />
          </div>
        ) : null}
        <div className={styles.searchResultsContent} data-search-results-content>
          {renderHits()}
        </div>
      </div>
      <Disclaimer disclaimers={window.Jbhifi.disclaimers} />
    </>
  );
};

export default GoogleSearchPanel;
